import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight, faThumbtack, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import Alert from "../../crmpostventa-shared/components/Alert";
import ChoiceGroup from "../../crmpostventa-shared/components/ChoiceGroup";
import { EMPTY, PRIORIDAD_LABELS, formatCuit, formatDate } from "../../crmpostventa-shared/crmFormat";
import { terminalOptionLabel } from "../../crmpostventa-rollo/nuevaGestion/nuevaGestionModel";
import { HistoricoChip, IpcStatusChip, TipoChip } from "../components/ipcCells";
import { MOTIVO_LABELS, isOpenGestion, motivoLabel } from "../ipcFormat";
import {
  CANAL_OPTIONS,
  ESTADO_INICIAL_OPTIONS,
  MOTIVO_CIERRE_OPTIONS,
  MOTIVO_PROACTIVA_OPTIONS,
  OBSERVACION_MAX,
  PRIORIDAD_OPTIONS,
  TIPO_OPTIONS,
  errorId,
  fieldId,
  formatDateTime,
  isProactivaCategoria,
  motivoOptions,
  observacionObligatoria,
  prioridadCalculada,
  similarAckText,
  similarTitle,
  terminalesDeContexto,
  toInputDateTime,
} from "./nuevaGestionIpcModel";

const ULTIMAS_CERRADAS = 3;

const describedBy = (errors, key) => (errors[key] ? { "aria-invalid": true, "aria-describedby": errorId(key) } : {});

function Field({ name, label, htmlFor, labelId, required = false, optional = false, full = false, errors, help, action, children }) {
  const LabelTag = htmlFor ? "label" : "span";
  const labelNode = (
    <LabelTag className="crm-rollo__fld-label" htmlFor={htmlFor} id={labelId}>
      {label}
      {required ? (
        <span className="crm-rollo__req" aria-hidden="true">
          {" *"}
        </span>
      ) : null}
      {optional ? <span className="crm-rollo__opt"> (opcional)</span> : null}
    </LabelTag>
  );
  return (
    <div className={`crm-rollo__fld${full ? " crm-rollo__fld--full" : ""}${errors[name] ? " has-error" : ""}`}>
      {action ? (
        <div className="crm-ipc__fld-head">
          {labelNode}
          {action}
        </div>
      ) : (
        labelNode
      )}
      {children}
      {errors[name] ? (
        <span className="crm-rollo__err" id={errorId(name)}>
          {errors[name]}
        </span>
      ) : help ? (
        <span className="crm-rollo__help">{help}</span>
      ) : null}
    </div>
  );
}

// Header of step 2 with the comercio and "Cambiar comercio" (back to step 1). A Proactiva from the Ficha shows its
// preloaded terminal here, because it has no terminal field (09-verificacion-diseno.md §7).
export function ComercioHeader({ comercio, terminal, disabled, onChangeComercio }) {
  const detail = [
    comercio?.cuit ? `CUIT ${formatCuit(comercio.cuit)}` : "",
    comercio?.numeroComercio ? `N° ${comercio.numeroComercio}` : "",
    comercio?.razonSocial || "",
    terminal ? `Terminal ${terminal}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="crm-rollo__client">
      <div>
        <div className="crm-rollo__rcard-name">{comercio?.nombre || comercio?.razonSocial || "Sin nombre"}</div>
        {detail ? <span className="crm-rollo__s">{detail}</span> : null}
      </div>
      <button type="button" className="crm-rollo__link" disabled={disabled} onClick={onChangeComercio}>
        Cambiar comercio
      </button>
    </div>
  );
}

export function ContextoLoading() {
  return (
    <div className="crm-rollo__context-skel" aria-busy="true" aria-label="Cargando datos del comercio">
      {["55%", "80%", "40%", "70%"].map((width) => (
        <span key={width} className="crm-rollo__skel crm-rollo__skel--tall" style={{ width }} aria-hidden="true" />
      ))}
    </div>
  );
}

export function ContextoError({ message, onRetry }) {
  return (
    <div className="crm-rollo__note crm-rollo__note--error" role="alert">
      <span className="crm-rollo__note-ico" aria-hidden="true">
        <FontAwesomeIcon icon={faTriangleExclamation} />
      </span>
      <b>No pudimos cargar los datos del comercio</b>
      <p>{message || "Probá de nuevo en unos segundos."}</p>
      <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" onClick={onRetry}>
        <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
        Reintentar
      </button>
    </div>
  );
}

function GestionComercio({ gestion }) {
  const open = isOpenGestion(gestion);
  return (
    <li>
      <div className="crm-ipc__ctx-row">
        <TipoChip item={gestion} />
        <span>{motivoLabel(gestion)}</span>
      </div>
      <div className="crm-ipc__ctx-row">
        <IpcStatusChip item={gestion} />
        {gestion.esHistorico ? <HistoricoChip /> : null}
        <span className="crm-rollo__muted">
          {open ? gestion.responsableNombre || "Sin asignar" : formatDate(gestion.fechaCierre || gestion.fechaAlta)}
        </span>
      </div>
    </li>
  );
}

// Read-only context of the comercio: its open gestiones and its last closed ones, históricos included (Prompt 2).
function ContextoGestiones({ contexto }) {
  const abiertas = contexto?.gestionesAbiertas || [];
  const cerradas = (contexto?.ultimasCerradas || []).slice(0, ULTIMAS_CERRADAS);
  const list = (items) => (
    <ul className="crm-ipc__ctx-list">
      {items.map((gestion) => (
        <GestionComercio key={`${gestion.esHistorico ? "h" : "g"}-${gestion.id}`} gestion={gestion} />
      ))}
    </ul>
  );

  return (
    <section className="crm-rollo__context" aria-label="Gestiones del comercio">
      <div>
        <h4>{`Gestiones abiertas (${abiertas.length})`}</h4>
        {abiertas.length ? list(abiertas) : <span className="crm-rollo__muted">Sin gestiones abiertas</span>}
      </div>
      <div>
        <h4>Últimas cerradas</h4>
        {cerradas.length ? list(cerradas) : <span className="crm-rollo__muted">Sin gestiones cerradas</span>}
      </div>
    </section>
  );
}

// Similar open gestión (CU9): a warning that never blocks; "Crear igual" leaves a short note instead (S4).
function SimilarAviso({ similar, ack, today, onView, onAck }) {
  if (!similar) return null;
  if (ack) return <p className="crm-rollo__help crm-rollo__fld--full">{similarAckText(similar)}</p>;
  return (
    <div className="crm-rollo__fld--full crm-ipc__similar">
      <Alert tone="warn" role="status" title={similarTitle(similar, today)}>
        Podés crear otra igual: un comercio puede tener varias gestiones abiertas.
      </Alert>
      <div className="crm-ipc__similar-actions">
        <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" onClick={onView}>
          Ver gestión
        </button>
        <button type="button" className="crm-rollo__btn crm-rollo__btn--ghost crm-rollo__btn--sm" onClick={onAck}>
          Crear igual
        </button>
      </div>
    </div>
  );
}

const PRIORIDAD_INICIAL = {
  ALTA: "Los reclamos empiezan en Alta.",
  MEDIA: "Las modificaciones y consultas empiezan en Media.",
};

// Priority is not a field (S6): the calculated one, and "Cambiar" opens a select that fixes it by hand.
function PrioridadAviso({ categoria, form, open, onOpen, onChange }) {
  const auto = prioridadCalculada(categoria, form.tipo);
  if (!auto) return <Alert tone="info" title="La prioridad se calcula al elegir el tipo." />;

  const inicial = isProactivaCategoria(categoria) ? "Las gestiones proactivas empiezan en Media." : PRIORIDAD_INICIAL[auto];
  const vence = form.estado === "RESUELTA" ? "" : " Pasa a Crítica si vence el próximo contacto.";
  return (
    <Alert tone="info" title={`Se creará con prioridad ${PRIORIDAD_LABELS[form.prioridad || auto]}`}>
      {form.prioridad ? (
        <span className="crm-ipc__manual">
          <FontAwesomeIcon icon={faThumbtack} aria-hidden="true" />
          Queda fijada a mano: el sistema ya no la recalcula.
        </span>
      ) : (
        <span>{`${inicial}${vence}`}</span>
      )}
      <div className="crm-ipc__prioridad">
        {open ? (
          <span className="crm-rollo__field crm-rollo__field--select">
            <select
              className="crm-rollo__select"
              id={fieldId("prioridad")}
              aria-label="Prioridad"
              value={form.prioridad}
              onChange={(event) => onChange("prioridad", event.target.value)}
            >
              <option value="">{`Automática (${PRIORIDAD_LABELS[auto]})`}</option>
              {PRIORIDAD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </span>
        ) : (
          <button type="button" className="crm-rollo__link" onClick={onOpen}>
            Cambiar
          </button>
        )}
      </div>
    </Alert>
  );
}

// What the system fills in by itself, shown without fields (dashed box).
function DatosAutomaticos({ categoria, asesorNombre, registro }) {
  return (
    <section className="crm-rollo__auto" aria-label="Datos automáticos">
      <div className="crm-rollo__auto-title">Se completa automáticamente</div>
      <dl>
        <div>
          <dt>Categoría</dt>
          <dd>{isProactivaCategoria(categoria) ? "Proactiva" : "IPC"}</dd>
        </div>
        <div>
          <dt>Origen</dt>
          <dd>
            <span className="crm-rollo__chip crm-rollo__chip--origen">Asesor</span>
          </dd>
        </div>
        <div>
          <dt>Responsable</dt>
          <dd>{asesorNombre || EMPTY}</dd>
        </div>
        <div>
          <dt>Fecha de registro</dt>
          <dd>{formatDateTime(registro)}</dd>
        </div>
      </dl>
    </section>
  );
}

// Terminal of an IPC gestión: optional; read-only when the comercio has one, a select when it has several.
function TerminalField({ terminales, value, errors, onChange }) {
  if (!terminales.length) return null;
  if (terminales.length === 1) {
    return (
      <Field name="terminal" label="Terminal" optional errors={errors} help="Única terminal del comercio">
        <span className="crm-rollo__t" id={fieldId("terminal")}>
          {terminales[0].numeroTerminal}
        </span>
      </Field>
    );
  }
  return (
    <Field name="terminal" label="Terminal" htmlFor={fieldId("terminal")} optional errors={errors}>
      <span className="crm-rollo__field crm-rollo__field--select">
        <select className="crm-rollo__select" id={fieldId("terminal")} value={value} onChange={(event) => onChange("terminal", event.target.value)}>
          <option value="">Sin terminal específica</option>
          {terminales.map((terminal) => (
            <option key={terminal.numeroTerminal} value={terminal.numeroTerminal}>
              {terminalOptionLabel(terminal)}
            </option>
          ))}
        </select>
      </span>
    </Field>
  );
}

/**
 * Step 2 · Datos de la gestión (design Prompt 2 for IPC, Prompt 3 for Proactiva). The form is controlled by
 * NuevaGestionIpcModal: validation runs on submit and each message goes under its field until the field changes.
 */
export default function GestionIpcStep({
  categoria,
  contexto,
  form,
  errors,
  saving,
  similar,
  ack,
  prioridadOpen,
  asesorNombre,
  registro,
  today,
  onChange,
  onOpenPrioridad,
  onViewSimilar,
  onAckSimilar,
  onSubmit,
}) {
  const proactiva = isProactivaCategoria(categoria);
  const resuelta = form.estado === "RESUELTA";
  const obligatoria = observacionObligatoria(categoria, form);
  const limite = toInputDateTime(registro);
  const inputProps = (key) => ({
    id: fieldId(key),
    value: form[key],
    onChange: (event) => onChange(key, event.target.value),
    ...describedBy(errors, key),
  });
  const choiceProps = (key, labelId) => ({
    id: fieldId(key),
    name: fieldId(key),
    value: form[key],
    onChange: (value) => onChange(key, value),
    labelledBy: labelId,
    describedBy: errors[key] ? errorId(key) : undefined,
    invalid: Boolean(errors[key]),
  });

  const motivoField = proactiva ? (
    <Field name="motivo" label="Motivo / acción" labelId="crm-ipc-nueva-motivo-label" required full errors={errors}>
      <ChoiceGroup options={MOTIVO_PROACTIVA_OPTIONS} {...choiceProps("motivo", "crm-ipc-nueva-motivo-label")} />
    </Field>
  ) : (
    <Field
      name="motivo"
      label="Motivo"
      htmlFor={fieldId("motivo")}
      required
      errors={errors}
      help={form.tipo ? null : "Elegí primero el tipo"}
    >
      <span className="crm-rollo__field crm-rollo__field--select">
        <select className="crm-rollo__select" disabled={!form.tipo} aria-required="true" {...inputProps("motivo")}>
          <option value="">{form.tipo ? "Elegí el motivo" : "Elegí primero el tipo"}</option>
          {motivoOptions(form.tipo).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </span>
    </Field>
  );

  const proximoHelp =
    proactiva && form.proximoAutomatico && form.proximoContacto
      ? "Precargado a 3 días por ser visita comercial. Podés cambiarlo o quitarlo."
      : "Si vence sin acción, la prioridad pasa a Crítica";

  return (
    <>
      <ContextoGestiones contexto={contexto} />
      <form
        className="crm-rollo__form-wrap"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <fieldset className="crm-rollo__form" disabled={saving}>
          <legend className="crm-rollo__sr-only">Datos de la gestión</legend>
          {proactiva ? null : (
            <Field name="tipo" label="Tipo" labelId="crm-ipc-nueva-tipo-label" required full errors={errors}>
              <ChoiceGroup options={TIPO_OPTIONS} {...choiceProps("tipo", "crm-ipc-nueva-tipo-label")} />
            </Field>
          )}
          {motivoField}
          {proactiva ? null : (
            <TerminalField terminales={terminalesDeContexto(contexto)} value={form.terminal} errors={errors} onChange={onChange} />
          )}
          <SimilarAviso similar={similar} ack={ack} today={today} onView={onViewSimilar} onAck={onAckSimilar} />

          {proactiva ? null : (
            <Field name="canal" label="Canal de contacto" labelId="crm-ipc-nueva-canal-label" required full errors={errors}>
              <ChoiceGroup options={CANAL_OPTIONS} {...choiceProps("canal", "crm-ipc-nueva-canal-label")} />
            </Field>
          )}

          <Field
            name="fechaContacto"
            label={proactiva ? "Fecha y hora" : "Fecha y hora del contacto"}
            htmlFor={fieldId("fechaContacto")}
            required
            errors={errors}
            help="Precargada con el momento actual"
          >
            <input className="crm-rollo__input" type="datetime-local" max={limite} {...inputProps("fechaContacto")} />
          </Field>

          {resuelta ? null : (
            <Field
              name="proximoContacto"
              label="Próximo contacto"
              htmlFor={fieldId("proximoContacto")}
              optional
              errors={errors}
              help={proximoHelp}
              action={
                proactiva && form.proximoContacto ? (
                  <button type="button" className="crm-rollo__link" onClick={() => onChange("proximoContacto", "")}>
                    Quitar
                  </button>
                ) : null
              }
            >
              <input className="crm-rollo__input" type="datetime-local" min={limite} {...inputProps("proximoContacto")} />
            </Field>
          )}

          <Field name="estado" label="Estado inicial" labelId="crm-ipc-nueva-estado-label" full errors={errors}>
            <ChoiceGroup options={ESTADO_INICIAL_OPTIONS} {...choiceProps("estado", "crm-ipc-nueva-estado-label")} />
          </Field>

          {resuelta ? (
            <>
              <div className="crm-rollo__fld--full">
                <Alert tone="info" title="La gestión se registra directamente como finalizada y va a Finalizadas." />
              </div>
              <Field name="motivoCierre" label="Motivo de cierre" htmlFor={fieldId("motivoCierre")} required errors={errors}>
                <span className="crm-rollo__field crm-rollo__field--select">
                  <select className="crm-rollo__select" aria-required="true" {...inputProps("motivoCierre")}>
                    <option value="">Elegí el motivo de cierre</option>
                    {MOTIVO_CIERRE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </span>
              </Field>
            </>
          ) : null}

          <Field
            name="observacion"
            label="Observación"
            htmlFor={fieldId("observacion")}
            required={obligatoria}
            optional={!obligatoria}
            full
            errors={errors}
          >
            <textarea
              className="crm-rollo__input crm-rollo__textarea"
              rows={3}
              maxLength={OBSERVACION_MAX}
              placeholder={proactiva ? "Qué acción vas a hacer con el comercio" : "Qué pidió el comercio y qué le respondiste"}
              aria-required={obligatoria || undefined}
              {...inputProps("observacion")}
            />
            <span className="crm-ipc__count">
              <span className="crm-rollo__help">
                {obligatoria && !errors.observacion
                  ? proactiva
                    ? "Obligatoria cuando el motivo es Otro"
                    : `Obligatoria para el motivo ${MOTIVO_LABELS[form.motivo] || form.motivo}`
                  : ""}
              </span>
              <span className="crm-rollo__help">{`${form.observacion.length}/${OBSERVACION_MAX}`}</span>
            </span>
          </Field>

          {proactiva ? (
            form.mostrarCanal ? (
              <Field
                name="canal"
                label="Canal"
                labelId="crm-ipc-nueva-canal-label"
                optional
                full
                errors={errors}
                action={
                  <button type="button" className="crm-rollo__link" onClick={() => onChange("mostrarCanal", false)}>
                    Quitar canal
                  </button>
                }
              >
                <ChoiceGroup options={CANAL_OPTIONS} {...choiceProps("canal", "crm-ipc-nueva-canal-label")} />
              </Field>
            ) : (
              <div className="crm-rollo__fld--full">
                <button type="button" className="crm-rollo__link" onClick={() => onChange("mostrarCanal", true)}>
                  + Agregar canal
                </button>
              </div>
            )
          ) : null}

          <div className="crm-rollo__fld--full">
            <PrioridadAviso categoria={categoria} form={form} open={prioridadOpen} onOpen={onOpenPrioridad} onChange={onChange} />
          </div>
        </fieldset>
      </form>
      <DatosAutomaticos categoria={categoria} asesorNombre={asesorNombre} registro={registro} />
    </>
  );
}
